
import {
    Box,
    Button,
    Divider,
    Link,
    Slide,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect } from 'react';
import Iconify from 'src/components/iconify';
import { useBoolean } from 'src/hooks/use-boolean';
import { useLocales } from 'src/locales';
import { paths } from 'src/routes/paths';

const CONSENT_KEY = 'site_cookie_consent';

export default function CookieConsent() {

    const open = useBoolean();

    const blocked = useBoolean();

    const { t } = useLocales();

    useEffect(() => {

        const consent = localStorage.getItem(CONSENT_KEY);

        if (!consent) {

            open.onTrue();
        } else if (consent === 'declined') {
            blocked.onTrue();
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem(CONSENT_KEY, 'accepted');
        open.onFalse();
        blocked.onFalse();
    };

    const handleDecline = () => {
        localStorage.setItem(CONSENT_KEY, 'declined');
        open.onFalse();
        blocked.onTrue();
    };

    return (

        <>

            <Slide direction="up" in={open.value} mountOnEnter unmountOnExit>
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 0, sm: 24 },
                        right: { xs: 0, sm: 24 },
                        zIndex: 1400,
                        width: { xs: '100%', sm: 420 },
                        bgcolor: 'background.paper',
                        borderRadius: { xs: '5px 5px 0 0', sm: 3 },
                        boxShadow: (theme) =>
                            `0 8px 40px ${theme.palette.mode === 'dark'
                                ? 'rgba(0,0,0,0.6)'
                                : 'rgba(0,0,0,0.15)'}`,
                        overflow: 'hidden',
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            bgcolor: 'primary.light',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Iconify icon="material-symbols:cookie" width={22} />
                        <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{ color: 'primary.contrastText' }}
                        >
                            Cookie Preferences
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Body */}
                    <Box sx={{ px: 3, py: 2.5 }}>
                        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                            {t('home.cookie_content')}{' '}
                            <Link
                                // href={paths.termsOfService}
                                underline="hover"
                                color="primary"
                                fontWeight={600}
                            >
                                {t('home.terms_of_conditions')}
                            </Link>{' '}
                            and{' '}
                            <Link
                                // href="/privacy-policy"
                                underline="hover"
                                color="primary"
                                fontWeight={600}
                            >
                                {t('home.privacy_policy')}
                            </Link>
                            .
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Actions */}
                    <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            size="small"
                            onClick={handleDecline}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                color: 'text.secondary',
                                borderColor: 'divider',
                                '&:hover': { borderColor: 'text.secondary' },
                            }}
                        >
                            {t('home.decline') ?? 'Decline'}
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleAccept}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                backgroundColor: "primary.light",
                                '&:hover': {
                                    boxShadow: 'none',
                                    backgroundColor: "primary.light",
                                },
                            }}
                        >
                            {t('home.accept')}
                        </Button>
                    </Stack>
                </Box>
            </Slide>

            {blocked.value && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2000,
                        bgcolor: 'background.default',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                        px: 3,
                        textAlign: 'center',
                    }}
                >
                    <Iconify icon="boxicons:block-filled" sx={{ fontSize: 64, color: 'text.disabled' }} />

                    <Box>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            {t('home.cookie_required')}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ maxWidth: 400, mx: 'auto', lineHeight: 1.8 }}
                        >
                            {t('home.cookie_required_content')}
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <Button
                            variant="contained"
                            onClick={handleAccept}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                px: 4,
                                backgroundColor: "primary.light",
                                '&:hover': {
                                    boxShadow: 'none',
                                    backgroundColor: "primary.light",
                                },
                            }}
                        >
                            {t("home.accept_cookie")}
                        </Button>

                        <Button
                            variant="outlined"
                            color="inherit"
                            href="https://support.google.com/chrome/answer/95647"
                            target="_blank"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                color: 'text.secondary',
                                borderColor: 'divider',
                                px: 4,
                                '&:hover': { borderColor: 'text.secondary' },
                            }}
                        >
                            {t("home.how_to_enable_cookies")}
                        </Button>
                    </Stack>
                </Box>
            )}
        </>
    );
}